import { ApiError, isValidIsoDate } from '@clockwise/shared';
import { DATE_RANGE_PRESETS } from '@clockwise/shared/date-range';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, Share2, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/custom/confirm-dialog';
import { NoAccess } from '@/components/custom/no-access';
import { shareReach } from '@/components/custom/share-badge';
import { PageHeader } from '@/components/layout/page-header';
import { PageShell } from '@/components/layout/page-shell';
import { decodeDrill, encodeDrill } from '@/components/reports/drill-chain';
import { ReportWorkbench } from '@/components/reports/report-workbench';
import type { BarSearch } from '@/components/reports/saved-report-view';
import { ShareReportDialog } from '@/components/reports/share-report-dialog';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { reportDefinitionsApi } from '@/lib/api';
import { useMe } from '@/lib/me';
import { queryKeys } from '@/lib/query-keys';

/**
 * One saved report — `/reports/custom/$reportId`.
 *
 * Opening it RUNS it. Nothing about the numbers is stored: the row carries a
 * spec, this page hands that spec to the analytics endpoint, and the endpoint
 * scopes the rows to whoever is signed in. So the same link sent to two people
 * shows each of them their own answer, and sending it discloses nothing.
 *
 * Reaching this page needs `report.view` alone — a recipient with no
 * `report.edit` can open, run and export what was shared with them (CR-1.5).
 * What they cannot do is save over it, share it or delete it; those controls
 * key off `report.owned`, which the server decides.
 */
export const Route = createFileRoute('/_authenticated/reports/custom/$reportId')({
  component: SavedReportPage,
  // Same `?sql=true` diagnostic as `/reports/new`, and the same reason it is a
  // param rather than a control. Optional in the parsed shape so a link to a
  // saved report does not have to carry it. Only the literal string counts:
  // `Boolean('false')` is `true`.
  // `?drill=` is the drill chain, so a drill is a link: the report, then each
  // click and where it landed. Decoded leniently — a mangled one is no drill,
  // not an error — see `decodeDrill`.
  //
  // `?edit=true` is edit mode; without it the report opens to be read. `f`,
  // `period`, `from` and `to` are the VIEWER's bar — see `saved-report-view.ts`
  // — each absent when it is as saved, so the plain link opens on the saved
  // report. A value that is not one of ours is dropped, never an error.
  validateSearch: (search: Record<string, unknown>): SavedReportSearch => ({
    ...(search.sql === 'true' || search.sql === true ? { sql: true as const } : {}),
    ...(typeof search.drill === 'string' && search.drill ? { drill: search.drill } : {}),
    ...(search.edit === 'true' || search.edit === true ? { edit: true as const } : {}),
    ...(typeof search.f === 'string' && search.f ? { f: search.f } : {}),
    ...((DATE_RANGE_PRESETS as readonly unknown[]).includes(search.period)
      ? { period: search.period as BarSearch['period'] }
      : {}),
    ...(isIsoDate(search.from) ? { from: search.from } : {}),
    ...(isIsoDate(search.to) ? { to: search.to } : {}),
  }),
});

interface SavedReportSearch extends BarSearch {
  sql?: true;
  drill?: string;
  edit?: true;
}

/** The shared definition of a real `YYYY-MM-DD` — 2026-02-31 is not one. */
const isIsoDate = (v: unknown): v is string => typeof v === 'string' && isValidIsoDate(v);

function SavedReportPage() {
  const { reportId } = Route.useParams();
  const {
    sql: includeSql = false,
    drill: drillParam,
    edit,
    f,
    period,
    from,
    to,
  } = Route.useSearch();
  // By the STRING: decoding makes a new array, and a new chain every render
  // would recompute every landing under it.
  const chain = useMemo(() => decodeDrill(drillParam), [drillParam]);
  const { permissions, loading } = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [sharing, setSharing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const report = useQuery({
    queryKey: queryKeys.reportDefinitions.detail(reportId),
    queryFn: () => reportDefinitionsApi.get(reportId).then((r) => r.data),
    // A report someone else edited should show its current definition, so this
    // is not cached indefinitely the way the model catalog is.
    staleTime: 30_000,
    retry: false,
  });

  const remove = useMutation({
    mutationFn: () => reportDefinitionsApi.remove(reportId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reportDefinitions.all });
      toast.success('Report deleted');
      navigate({ to: '/reports' });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Could not delete'),
  });

  const mayEdit = permissions.has('report.edit');
  const mayShare = permissions.has('report.share');

  if (!loading && !permissions.has('report.view')) {
    return <NoAccess permission="report.view" resource="reports" />;
  }

  // `loading` AND NOT JUST THE REPORT. `useMe` reports an EMPTY permission set
  // while its query is in flight, so `mayEdit` is provisionally false until it
  // lands — and this page's three requests race: `/api/users/me`, this report,
  // and the model catalog. Rendering on the first two alone mounted the
  // workbench with `mayEdit: false` whenever `me` came last, which drew the
  // builder with NO configuration column and no Save, then popped both in a
  // beat later. A guard on `mayEdit` itself would not have helped: false is
  // exactly what an empty set already answers. The page has to wait.
  //
  // `/reports/new` never had the bug because its permission gate withholds the
  // whole page on `loading`; this is that, in the shape this route needs.
  if (loading || report.isPending) {
    return (
      <PageShell>
        <Skeleton className="h-9 w-72" />
        <Skeleton className="h-96 w-full" />
      </PageShell>
    );
  }

  // G15: a report that is gone, or was never shared with this caller, is a
  // legible state and not a blank page. The server answers NOT_FOUND for both —
  // deliberately, since "there is a report here you may not open" is itself a
  // disclosure — so this message covers the two without claiming to tell them
  // apart.
  if (report.isError) {
    const missing = report.error instanceof ApiError && report.error.code === 'NOT_FOUND';
    return (
      <PageShell>
        <PageHeader title="Report" />
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/5 px-3 py-2 text-sm"
        >
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden />
          <p className="min-w-0">
            <span className="font-medium text-destructive">
              {missing ? 'This report isn’t available.' : 'Couldn’t load this report.'}
            </span>{' '}
            {missing
              ? 'It may have been deleted, or it isn’t shared with you.'
              : report.error instanceof ApiError
                ? report.error.message
                : 'Please try again.'}
          </p>
        </div>
      </PageShell>
    );
  }

  const saved = report.data;
  // The server's answer — owner, or a recipient allowed to reshare, and either
  // way with `report.share`. The fallback is for an API older than resharing.
  const canShare = saved.canShare ?? (saved.owned && mayShare);
  const canDelete = saved.owned && mayEdit;

  return (
    <PageShell>
      {/* The workbench draws the whole header — the report's name, its
          description, and every action on it. Share and Delete are handed to it
          as MENU ITEMS: they belong to a SAVED report and only exist on this
          route, which is why they are built here rather than inside a component
          `/reports/new` also uses. */}
      <ReportWorkbench
        report={saved}
        // Only ever seen by a report saved without a name, which the naming
        // dialog makes hard to do — the heading is the report's own name, read
        // off the draft so a rename shows where it was made.
        fallbackTitle="Untitled report"
        includeSql={includeSql}
        mayEdit={mayEdit}
        view={{
          editing: edit === true,
          onEditingChange: (next) =>
            navigate({
              to: '.',
              search: (prev) => ({ ...prev, edit: next ? true : undefined }),
              replace: true,
            }),
          search: { f, period, from, to },
          // REPLACE, like the drill: the viewer's bar is one state of one page.
          onSearchChange: (next) =>
            navigate({
              to: '.',
              search: (prev) => ({
                ...prev,
                f: next.f,
                period: next.period,
                from: next.from,
                to: next.to,
              }),
              replace: true,
            }),
        }}
        drill={{
          chain,
          // REPLACE, not push: each level is one state of one page, and Back
          // in the browser should leave the report rather than walk the tray.
          onChange: (next) =>
            navigate({
              to: '.',
              search: (prev) => ({ ...prev, drill: encodeDrill(next) }),
              replace: true,
            }),
        }}
        menuItems={
          // Delete is owner-only. Share is the owner's, or a recipient's whose
          // share lets them reshare — and needs `report.share` either way:
          // someone who may build reports is not thereby someone who may hand
          // them around.
          canShare || canDelete ? (
            <>
              {canShare && (
                <DropdownMenuItem onSelect={() => setSharing(true)}>
                  <Share2 className="size-4" />
                  Share
                </DropdownMenuItem>
              )}
              {canDelete && (
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  disabled={remove.isPending}
                  onSelect={() => setConfirmingDelete(true)}
                >
                  <Trash2 className="size-4" />
                  Delete
                </DropdownMenuItem>
              )}
            </>
          ) : undefined
        }
      />

      {sharing && <ShareReportDialog report={saved} open={sharing} onOpenChange={setSharing} />}

      <ConfirmDialog
        open={confirmingDelete}
        title={`Delete “${saved.name}”?`}
        description={
          saved.shares.length > 0
            ? `This report is shared with ${shareReach(saved.shares)}. Deleting it removes it from their list too, and can’t be undone.`
            : 'This can’t be undone.'
        }
        confirmLabel="Delete"
        destructive
        confirmDisabled={remove.isPending}
        onConfirm={() => {
          setConfirmingDelete(false);
          remove.mutate();
        }}
        onCancel={() => setConfirmingDelete(false)}
      />
    </PageShell>
  );
}
